import React, { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db, functions } from '../../core/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Building2, Mail, Lock, ArrowRight, ShieldCheck, Globe } from 'lucide-react';
import { initialUserProfile } from '../../core/constants/initialStates';

const SignOnScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [agreedToTos, setAgreedToTos] = useState(false);
  const [isEnterpriseMode, setIsEnterpriseMode] = useState(false); 

  const determineLicense = (email) => {
    const tenant_domains = ['linkedin.com', 'workday.com', 'enterprise.com'];
    const domain = email.split('@')[1];
    return tenant_domains.includes(domain) ? 'PRO' : 'ESSENTIALS';
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!isLogin && !agreedToTos) {
      setError("You must agree to the Terms of Service, Privacy Policy, and confirm you are 16+.");
      return;
    }

    try {
      let userCredential;
      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenString = urlParams.get('token');
        
        let targetRole = 'candidate'; 
        
        if (tokenString) {
            try {
                const verifySponsorToken = httpsCallable(functions, 'verifySponsorToken');
                const verificationResult = await verifySponsorToken({ invitationToken: tokenString });
                if (verificationResult.data?.isValid) {
                    targetRole = verificationResult.data.roleId; 
                }
            } catch (tokenErr) {
                console.error("[Token Verification Rejected]", tokenErr);
                setError("Invalid or tampered enterprise invite token. Registration aborted.");
                return;
            }
        }

        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const license = determineLicense(email);
        
        await setDoc(doc(db, 'users', user.uid), {
          ...initialUserProfile,
          email: user.email,
          role: targetRole, 
          subscriptionType: targetRole === 'sponsor' ? 'ESSENTIALS' : license,
          enterpriseTenantId: isEnterpriseMode ? 'enterprise_corp' : 'public',
          createdAt: serverTimestamp()
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
         await setDoc(doc(db, 'users', user.uid), {
          ...initialUserProfile,
          email: user.email,
          role: 'candidate',
          subscriptionType: 'ESSENTIALS', 
          enterpriseTenantId: 'public',
          createdAt: serverTimestamp()
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
        
        <div className={`p-8 text-center ${isEnterpriseMode ? 'bg-indigo-900' : 'bg-slate-900'} transition-colors duration-500`}>
          <div className="w-16 h-16 bg-white/10 rounded-2xl mx-auto flex items-center justify-center backdrop-blur-md mb-4 shadow-inner border border-white/20">
            {isEnterpriseMode ? <Building2 className="text-white" size={32} /> : <Globe className="text-white" size={32} />}
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">JobVantage</h1>
          <p className="text-blue-200 text-sm font-medium mt-1">
            {isEnterpriseMode ? "Enterprise SSO" : "Resilience Validation Beyond the Resume"}
          </p>
        </div>

        <div className="flex border-b border-slate-100">
            <button 
                onClick={() => setIsEnterpriseMode(false)}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest ${!isEnterpriseMode ? 'text-slate-900 border-b-2 border-slate-900 bg-slate-50' : 'text-slate-400 hover:bg-slate-50'}`}
            >
                Open Platform
            </button>
            <button 
                onClick={() => setIsEnterpriseMode(true)}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 ${isEnterpriseMode ? 'text-indigo-900 border-b-2 border-indigo-900 bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
            >
                <ShieldCheck size={14} /> Enterprise SSO
            </button>
        </div>

        <div className="p-8 space-y-6">
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                {isEnterpriseMode ? "Corporate Email" : "Email Address"}
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 text-slate-400" size={18} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none font-medium text-slate-700 transition-all"
                  placeholder={isEnterpriseMode ? "talent@enterprise.com" : "name@example.com"}
                  required 
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-3.5 text-slate-400" size={18} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none font-medium text-slate-700 transition-all"
                  placeholder="••••••••"
                  required 
                />
              </div>
            </div>

            {!isLogin && (
              <div className="flex items-start gap-2 mt-4">
                <input 
                  type="checkbox" 
                  id="tos" 
                  checked={agreedToTos} 
                  onChange={(e) => setAgreedToTos(e.target.checked)} 
                  className="mt-1 flex-shrink-0"
                />
                <label htmlFor="tos" className="text-xs text-slate-500 leading-snug">
                  I am 16+ and agree to the Terms of Service. I explicitly consent to the capture and processing of asynchronous kinematic metadata (e.g., cursor telemetry, interaction latency) for enterprise verification purposes.
                </label>
              </div>
            )}

            {error && <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-lg text-center">{error}</div>}

            <button type="submit" className={`w-full py-4 rounded-xl text-white font-black text-lg shadow-lg flex items-center justify-center gap-2 transform transition-all active:scale-[0.98] ${isEnterpriseMode ? 'bg-indigo-900 hover:bg-indigo-800' : 'bg-slate-900 hover:bg-black'} mt-2`}>
              {isLogin ? (isEnterpriseMode ? "Access Corporate Portal" : "Launch Simulator") : "Create Account"} 
              <ArrowRight size={20} />
            </button>
          </form>

          {!isEnterpriseMode && (
            <>
                <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
                    <div className="relative flex justify-center"><span className="bg-white px-2 text-xs text-slate-400 uppercase font-bold">Or continue with</span></div>
                </div>
                <button 
                    onClick={handleGoogle}
                    className="w-full py-3 bg-white border-2 border-slate-100 text-slate-600 font-bold rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-6.98 7.18-6.98 1.95 0 3.92.72 5.37 2.02l1.9-2.02C17.3 2.93 14.76 2 12 2 6.48 2 2 6.48 2 12s4.48 10 10 10c5.85 0 10-4.16 10-10 0-.76-.1-1.5-.2-2.3z"/></svg>
                    Google
                </button>
            </>
          )}

          <div className="text-center">
            <button onClick={() => setIsLogin(!isLogin)} className="text-xs font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wide">
              {isLogin ? "Need an account? Sign Up" : "Already have an account? Log In"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignOnScreen;